import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AuthContext } from '../App';

const API_URL = process.env.REACT_APP_API_URL;

// Tema por defecto (colores actuales del sistema)
export const DEFAULT_THEME = {
  primary: '#3cbf9a',
  secondary: '#1a73e8',
  primaryDark: '#2da080',
  primaryLight: 'rgba(60, 191, 154, 0.1)',
  primaryBorder: 'rgba(60, 191, 154, 0.2)',
  logoFilename: null,    // ← compatibilidad: nombre de archivo en /logos/
  logoArchivoId: null,   // ← id del archivo de logo en BD
  logoUrl: null,         // ← URL resuelta del logo (null = logo por defecto de la app)
};

/**
 * Dado un color hex primario, genera las variantes necesarias (dark, light, border).
 * Acepta opcionalmente datos de logo (archivo_id / filename) para resolver la URL final.
 */
function buildTheme(primaryHex, secondaryHex, logoFilename = null, logoArchivoId = null) {
  const primary   = primaryHex   || DEFAULT_THEME.primary;
  const secondary = secondaryHex || DEFAULT_THEME.secondary;

  // Versión oscura: mezcla el color con negro ~20%
  const primaryDark   = shadeColor(primary, -20);
  // Versión clara: el color con 10% de opacidad
  const primaryLight  = hexToRgba(primary, 0.1);
  const primaryBorder = hexToRgba(primary, 0.2);

  // Resolución de la URL del logo con prioridad: archivo BD > filename en /logos/ > defecto
  const logoUrl = logoArchivoId
    ? `${API_URL}/api/archivos/${logoArchivoId}`
    : logoFilename
      ? `${API_URL}/logos/${logoFilename}`
      : null;

  return {
    primary,
    secondary,
    primaryDark,
    primaryLight,
    primaryBorder,
    logoFilename:  logoFilename  || null,   // ← compatibilidad
    logoArchivoId: logoArchivoId || null,
    logoUrl,
  };
}

/** Oscurece / aclara un hex. amount negativo = más oscuro */
function shadeColor(hex, amount) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r   = Math.min(255, Math.max(0, (num >> 16) + amount));
  const g   = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amount));
  const b   = Math.min(255, Math.max(0, (num & 0x0000ff) + amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

/** Convierte hex a rgba */
function hexToRgba(hex, alpha) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r   = (num >> 16) & 255;
  const g   = (num >> 8) & 255;
  const b   =  num & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export const ThemeContext = createContext(DEFAULT_THEME);

export function ThemeProvider({ children }) {
  const { user, isAuthenticated } = useContext(AuthContext);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [themeReady, setThemeReady] = useState(false); // ← NUEVO

  const applyThemeToDOM = useCallback((t) => {
    const root = document.documentElement;
    root.style.setProperty('--color-primary',        t.primary);
    root.style.setProperty('--color-secondary',      t.secondary);
    root.style.setProperty('--color-primary-dark',   t.primaryDark);
    root.style.setProperty('--color-primary-light',  t.primaryLight);
    root.style.setProperty('--color-primary-border', t.primaryBorder);
  }, []);

  const loadClientTheme = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setTheme(DEFAULT_THEME);
      applyThemeToDOM(DEFAULT_THEME);
      setThemeReady(true); // ← páginas públicas pueden renderizar ya
      return;
    }

    try {
      const token  = localStorage.getItem('token');
      const rootId = user.clinica_matriz && user.clinica_matriz > 0
        ? user.clinica_matriz
        : user.clinica_id || user.id;

      if (!rootId) {
        setTheme(DEFAULT_THEME);
        applyThemeToDOM(DEFAULT_THEME);
        setThemeReady(true);
        return;
      }

      const response = await fetch(`${API_URL}/api/clientes/${rootId}/theme`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        setTheme(DEFAULT_THEME);
        applyThemeToDOM(DEFAULT_THEME);
      } else {
        const data  = await response.json();
        const built = buildTheme(data.color_primario, data.color_secundario, data.logo_filename, data.logo_archivo_id);
        setTheme(built);
        applyThemeToDOM(built);
      }
    } catch {
      setTheme(DEFAULT_THEME);
      applyThemeToDOM(DEFAULT_THEME);
    } finally {
      setThemeReady(true); // ← siempre se marca como listo al terminar
    }
  }, [isAuthenticated, user, applyThemeToDOM]);

  useEffect(() => {
    setThemeReady(false); // ← resetear al cambiar usuario
    loadClientTheme();
  }, [loadClientTheme]);

  return (
    <ThemeContext.Provider value={{ theme, themeReady, reloadTheme: loadClientTheme, buildTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

/** Hook de conveniencia */
export function useTheme() {
  return useContext(ThemeContext);
}